/**
 * GraduateToExpertPrompt.jsx — iter975
 * ─────────────────────────────────────────────────────────────────────────
 * Auto-graduation banner shown to Basic-view users once they:
 *   • Have been registered ≥ 7 days AND
 *   • Have logged ≥ 5 meals (`hiSummary.metabolic.meal_snapshots_count`)
 *
 * Presents an unobtrusive, dismissable card inviting the patient to
 * switch to Expert view. Persistence keys:
 *   • `graduation-prompt-dismissed-at`   — ISO timestamp of last dismiss
 *   • `graduation-prompt-shown-count`    — increments each display session
 *
 * We re-show the prompt at most once every 14 days after a dismiss, and
 * never re-prompt after the user has manually toggled to Expert once.
 *
 * Micro-progression philosophy: users grow into complexity instead of
 * being overwhelmed on day 1.
 */

import React, { useEffect, useState } from "react";
import { GraduationCap, Sparkles, X } from "lucide-react";

const DISMISS_KEY   = "graduation-prompt-dismissed-at";
const REPROMPT_DAYS = 14;

function _daysSince(iso) {
  if (!iso) return Infinity;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return Infinity;
  return (Date.now() - t) / (1000 * 60 * 60 * 24);
}

export default function GraduateToExpertPrompt({
  user,
  hiSummary,
  viewMode,
  onGraduate,
  isDark,
  bg = true,
}) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Only relevant while patient is in Basic view.
    if (viewMode !== "basic") return;

    const daysRegistered = _daysSince(user?.created_at);
    const mealsLogged    = hiSummary?.metabolic?.meal_snapshots_count ?? 0;
    if (daysRegistered < 7)  return;
    if (mealsLogged   < 5)   return;

    // Respect prior dismissal (re-prompt after REPROMPT_DAYS).
    try {
      const dismissedAt = localStorage.getItem(DISMISS_KEY);
      if (dismissedAt && _daysSince(dismissedAt) < REPROMPT_DAYS) return;
    } catch { /* localStorage unavailable */ }

    setVisible(true);
  }, [user?.created_at, hiSummary?.metabolic?.meal_snapshots_count, viewMode]);

  if (!visible) return null;

  const dismiss = () => {
    setVisible(false);
    try {
      localStorage.setItem(DISMISS_KEY, new Date().toISOString());
    } catch {}
  };

  const graduate = () => {
    setVisible(false);
    try {
      localStorage.setItem(DISMISS_KEY, new Date().toISOString());
    } catch {}
    onGraduate?.();
  };

  return (
    <div
      data-testid="graduation-prompt"
      className={`relative rounded-2xl p-4 sm:p-5 border overflow-hidden ${
        isDark
          ? "bg-gradient-to-br from-fuchsia-950/50 via-slate-900/40 to-violet-950/40 border-fuchsia-700/40"
          : "bg-gradient-to-br from-fuchsia-50 via-white to-violet-50 border-fuchsia-200"
      }`}
    >
      <button
        type="button"
        onClick={dismiss}
        aria-label={bg ? "Скрий" : "Dismiss"}
        data-testid="graduation-prompt-dismiss"
        className={`absolute top-2 right-2 p-1 rounded-full transition ${
          isDark
            ? "text-slate-400 hover:bg-white/10"
            : "text-slate-500 hover:bg-slate-100"
        }`}
      >
        <X className="w-4 h-4" />
      </button>

      <div className="flex items-start gap-3">
        <div
          className={`flex-shrink-0 w-11 h-11 rounded-2xl grid place-items-center ${
            isDark ? "bg-fuchsia-500/20" : "bg-fuchsia-100"
          }`}
        >
          <GraduationCap
            className={`w-6 h-6 ${isDark ? "text-fuchsia-300" : "text-fuchsia-700"}`}
          />
        </div>
        <div className="flex-1 min-w-0 pr-4">
          <div className={`text-sm sm:text-base font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
            🎓 {bg ? "Готов ли си за Разширен изглед?" : "Ready for Expert view?"}
          </div>
          <p className={`text-[11px] sm:text-xs mt-1 leading-relaxed ${isDark ? "text-slate-300" : "text-slate-600"}`}>
            {bg
              ? "Ще отключиш Auto-diagnosis, Digital Twin и метаболитен профил — още повече insights от твоите данни."
              : "Unlock Auto-diagnosis, Digital Twin and metabolic profile — deeper insights from your data."}
          </p>
          <div className="flex flex-wrap gap-2 mt-3">
            <button
              type="button"
              onClick={graduate}
              data-testid="graduation-prompt-accept"
              className={`inline-flex items-center gap-1.5 text-xs font-bold px-3.5 py-1.5 rounded-full transition ${
                isDark
                  ? "bg-fuchsia-500 text-white hover:bg-fuchsia-400"
                  : "bg-gradient-to-r from-fuchsia-500 to-violet-500 text-white hover:brightness-105 shadow-sm"
              }`}
            >
              <Sparkles className="w-3.5 h-3.5" />
              {bg ? "Активирай Разширен" : "Switch to Expert"}
            </button>
            <button
              type="button"
              onClick={dismiss}
              data-testid="graduation-prompt-later"
              className={`text-xs font-medium px-3 py-1.5 rounded-full transition ${
                isDark
                  ? "text-slate-400 hover:bg-white/5"
                  : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              {bg ? "По-късно" : "Later"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
